import { Button, Intent, Menu, MenuItem, Popover } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import {
    segmentTrackAdvancedStudiesNewAdvancedStudyMenuItemClicked,
    segmentTrackAdvancedStudiesViewAdvancedStudiesMenuItemClicked,
    segmentTrackFavoriteBusToggled,
    segmentTrackPerformDetailedAnalysisClicked,
    segmentTrackSelectionCanceledFromBusDetailPaneCross
} from "analytics/analyticTrackEvents";
import ViewAdvancedStudiesDialog from "components/advancedStudies/ViewAdvancedStudiesDialog";
import BusDot from "components/common/BusDot";
import { useAdvancedStudyContext } from "contexts/AdvancedStudyContext/AdvancedStudyContext";
import { useFavoriteBuses } from "contexts/FavoriteBusesContext/FavoriteBusesContext";
import useMapViewConfiguration from "contexts/MapViewConfigurationContext/hooks/useMapViewConfiguration";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import useSelectedBuses from "contexts/SelectedBusesContext/useSelectedBuses";
import useSelectedScopedBus from "contexts/SelectedScopedBusContext/useSelectedScopedBus";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { getUrlForBusPage } from "infrastructure/RouterApp";
import React, { useState } from "react";
import {
    getCapacityColor,
    getConstraintCountColor
} from "sharedStyles/capacityColorStyles";
import { DEFAULT_MW_SIZE } from "types/advancedStudyType";
import {
    ColorMode,
    DEFAULT_CONSTRAINT_COUNT_THRESHOLD
} from "types/busFilterTypes";
import { isLowConfidenceLocationBus, isOutOfRegionBus } from "types/busType";
import { LOAD_TYPE } from "types/generatorType";
import { RegionType } from "types/regionType";
import {
    canUseAdvancedStudies,
    isLocalStudiesEnabledForRegion
} from "types/scope";
import { maybeGetGenerationFuelType } from "types/scopeViewType";
import { getLoadOnlyFlag } from "types/teamFeatureFlagType";
import {
    LOW_CONFIDENCE_LOCATION_CALLOUT_BODY,
    LOW_CONFIDENCE_LOCATION_CALLOUT_TITLE,
    RTOE_CALLOUT_BODY,
    RTOE_CALLOUT_TITLE
} from "../shared/busCalloutMessages";
import DetailPaneCallout from "../shared/DetailPaneCallout";
import DetailPaneHeader from "../shared/DetailPaneHeader";
import "./BusDetailPane.scss";
import OtherBusesToggle from "./OtherBusesToggle";

type BusDetailPaneProps = {
    readonly energyTag: React.ReactNode;
    readonly chargingTag?: React.ReactNode;
    readonly capacityTag?: React.ReactNode;
    readonly statusChip?: React.ReactNode;
    readonly pilCallout?: React.ReactNode;
    readonly constraintDetailsSections: ReadonlyArray<React.ReactNode>;
    readonly capacityCostsGraph?: React.ReactNode;
    readonly connectedLinesSection: React.ReactNode;
    readonly canAccessLocalStudies?: boolean;
};

export type PowerAmounts = {
    readonly energySize: number;
    readonly capacitySize: number;
    readonly chargingSize: number;
};

const BusDetailPane: React.FC<BusDetailPaneProps> = (props) => {
    const {
        energyTag,
        chargingTag,
        capacityTag,
        statusChip,
        pilCallout,
        constraintDetailsSections,
        capacityCostsGraph,
        connectedLinesSection,
        canAccessLocalStudies = true
    } = props;
    const { clearSelectedBuses } = useSelectedBuses();
    const {
        generator,
        region,
        componentConfig: { showLineTaps }
    } = useMapViewRoutingMetadata();
    const scopedBus = useSelectedScopedBus();
    const {
        busFiltersConfiguration: { busFilters }
    } = useMapViewConfiguration();
    const { colorMode, capacityThresholds, constraintCountThresholds } =
        busFilters;
    const user = useUserDataContext();
    const isColorblindMode = user.isColorblindMode;
    const { addBus, openNewStudyDialog } = useAdvancedStudyContext();
    const { isFavorite, toggleFavorite } = useFavoriteBuses();
    const [
        isViewAdvancedStudiesDialogOpen,
        setIsViewAdvancedStudiesDialogOpen
    ] = useState(false);

    const isConstraintCountMode =
        region === RegionType.CAISO && colorMode === ColorMode.CONSTRAINT_COUNT;

    const toggleFavoriteWithTracking = (): void => {
        const { niraBusId } = scopedBus.bus;
        const favorited = !isFavorite(niraBusId);
        toggleFavorite(niraBusId);
        segmentTrackFavoriteBusToggled(niraBusId, favorited, "detail_pane");
    };

    const dotColor = isConstraintCountMode
        ? getConstraintCountColor(
              scopedBus.zeroMwConstraintCount ?? 0,
              constraintCountThresholds ?? DEFAULT_CONSTRAINT_COUNT_THRESHOLD,
              isColorblindMode
          )
        : getCapacityColor(
              scopedBus.scopedCapacityEnergyCost,
              capacityThresholds,
              generator,
              isColorblindMode
          );

    const onCancel = () => {
        segmentTrackSelectionCanceledFromBusDetailPaneCross(scopedBus.bus.id);
        clearSelectedBuses();
    };

    const showAdvancedStudiesMenu = canUseAdvancedStudies(
        user,
        region,
        busFilters.scope
    );

    const advancedStudiesMenu = showAdvancedStudiesMenu ? (
        <Menu className="BusDetailPane-advanced-studies-menu">
            <MenuItem
                text="New Advanced Study"
                icon={IconNames.ADD}
                onClick={() => {
                    segmentTrackAdvancedStudiesNewAdvancedStudyMenuItemClicked();
                    openNewStudyDialog();
                    addBus({
                        niraBusId: scopedBus.bus.niraBusId,
                        busDisplayName: scopedBus.bus.busDisplayName,
                        studyMwSize: DEFAULT_MW_SIZE
                    });
                }}
            />
            <MenuItem
                text="View Advanced Studies"
                icon={IconNames.EYE_OPEN}
                onClick={() => {
                    segmentTrackAdvancedStudiesViewAdvancedStudiesMenuItemClicked(
                        {
                            source: "bus_detail_pane"
                        }
                    );
                    setIsViewAdvancedStudiesDialogOpen(true);
                }}
            />
        </Menu>
    ) : undefined;

    // The bus page only studies generation, so load-only teams get no link
    // to it. BusPageConfigurationProvider refuses them on a direct URL too.
    const isLoadOnlyTeam = user.teamFeatureFlags.includes(
        getLoadOnlyFlag(region)
    );
    const showLocalStudiesButton =
        generator !== LOAD_TYPE &&
        canAccessLocalStudies &&
        !isLoadOnlyTeam &&
        isLocalStudiesEnabledForRegion(region);

    const maybeOpenLocalStudies = showLocalStudiesButton
        ? () => {
              segmentTrackPerformDetailedAnalysisClicked(
                  scopedBus.bus.id,
                  scopedBus.bus.niraBusId
              );
              window.open(
                  getUrlForBusPage(
                      scopedBus.bus.niraBusId,
                      maybeGetGenerationFuelType(
                          scopedBus.bus.region,
                          generator
                      )
                  ),
                  "_blank"
              );
          }
        : undefined;

    return (
        <>
            <div className="BusDetailPane-wrapper">
                <div className="BusDetailPane-main-content-wrapper">
                    <div className="BusDetailPane-scroll-region">
                        <DetailPaneHeader
                            title={scopedBus.bus.busDisplayName}
                            icon={<BusDot color={dotColor} />}
                            onCancel={onCancel}
                            energyTag={energyTag}
                            chargingTag={chargingTag}
                            capacityTag={capacityTag}
                            statusChip={statusChip}
                            isFavorite={isFavorite(scopedBus.bus.niraBusId)}
                            onToggleFavorite={toggleFavoriteWithTracking}
                        />
                        {pilCallout}
                        {region === RegionType.WECC &&
                            isOutOfRegionBus(scopedBus.bus) && (
                                <DetailPaneCallout
                                    className="BusDetailPane-rtoe-callout"
                                    intent={Intent.DANGER}
                                    title={RTOE_CALLOUT_TITLE}
                                >
                                    {RTOE_CALLOUT_BODY}
                                </DetailPaneCallout>
                            )}
                        {constraintDetailsSections.map(
                            (constraintDetailSection, index) => (
                                <div
                                    key={index}
                                    className="BusDetailPane-details-wrapper"
                                >
                                    {constraintDetailSection}
                                </div>
                            )
                        )}
                        <div>
                            <h6 className="bp5-heading">Bus details</h6>
                            {getBusDetailPaneRow(
                                "Bus ID",
                                `${scopedBus.bus.id}`
                            )}
                            {getBusDetailPaneRow(
                                "Voltage",
                                `${scopedBus.bus.voltage}`
                            )}
                            {getBusDetailPaneRow(
                                "Owner",
                                scopedBus.bus.ownerName
                            )}
                            {getBusDetailPaneRow(
                                "Coordinates",
                                `(${scopedBus.bus.latitude.toFixed(
                                    3
                                )}, ${scopedBus.bus.longitude.toFixed(3)})`
                            )}
                            {isLowConfidenceLocationBus(scopedBus.bus) && (
                                <DetailPaneCallout
                                    className="BusDetailPane-low-confidence-location-callout"
                                    intent={Intent.WARNING}
                                    title={
                                        LOW_CONFIDENCE_LOCATION_CALLOUT_TITLE
                                    }
                                >
                                    {LOW_CONFIDENCE_LOCATION_CALLOUT_BODY}
                                </DetailPaneCallout>
                            )}
                        </div>
                        {capacityCostsGraph}
                        {showLineTaps && connectedLinesSection}
                    </div>
                    {advancedStudiesMenu && (
                        <div className="BusDetailPane-footer">
                            <Popover
                                content={advancedStudiesMenu}
                                placement="bottom"
                                minimal
                                fill
                                matchTargetWidth
                            >
                                <Button
                                    intent="primary"
                                    fill
                                    rightIcon={IconNames.CARET_DOWN}
                                >
                                    Advanced studies
                                </Button>
                            </Popover>
                        </div>
                    )}
                    {maybeOpenLocalStudies && (
                        <div className="BusDetailPane-footer">
                            <Button
                                intent="primary"
                                fill
                                rightIcon={IconNames.SHARE}
                                onClick={maybeOpenLocalStudies}
                            >
                                Open Bus Details Page
                            </Button>
                        </div>
                    )}
                </div>
                <OtherBusesToggle />
            </div>
            <ViewAdvancedStudiesDialog
                isOpen={isViewAdvancedStudiesDialogOpen}
                onClose={() => setIsViewAdvancedStudiesDialogOpen(false)}
                selectedBus={scopedBus.bus}
            />
        </>
    );
};

const getBusDetailPaneRow = (label: string, value: string): JSX.Element => {
    return (
        <div className="BusDetailPane-row">
            <div className="BusDetailPane-row-label  bp5-text-disabled">
                {label}
            </div>
            <div className="BusDetailPane-row-value">{value}</div>
        </div>
    );
};

export default BusDetailPane;
